import React, { useState, useEffect, useCallback } from "react"
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import { useTranslation } from "react-i18next"
import apiClient from "../../services/apiClient"
import * as customerTypesService from "../../services/customerTypesService"

const thStyle = {
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase" as const,
    color: "#6b7280",
    letterSpacing: "0.05em",
}

const CustomerTypesAdminPage: React.FC = () => {
    const { t } = useTranslation()
    const [companyId, setCompanyId] = useState("")
    const [customerTypes, setCustomerTypes] = useState<any[]>([])
    const [openDialog, setOpenDialog] = useState(false)
    const [editingType, setEditingType] = useState<any>(null)
    const [formData, setFormData] = useState({ name: "", description: "" })
    const [formError, setFormError] = useState("")
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deletingType, setDeletingType] = useState<any>(null)
    const [deleteError, setDeleteError] = useState("")

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const res = await apiClient.get("/api/companies")
                if (cancelled) return
                const list = res.companies || []
                if (list.length > 0) setCompanyId(list[0].id)
            } catch (e: any) {
                console.error("Failed to load companies:", e)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    const loadCustomerTypes = useCallback(async () => {
        if (!companyId) return
        try {
            const types = await customerTypesService.listCustomerTypes(
                companyId
            )
            setCustomerTypes(types)
        } catch (error) {
            console.error("Failed to load customer types:", error)
        }
    }, [companyId])

    useEffect(() => {
        loadCustomerTypes()
    }, [loadCustomerTypes])

    const handleOpenDialog = (ct?: any) => {
        setFormError("")
        if (ct) {
            setEditingType(ct)
            setFormData({
                name: ct.name || "",
                description: ct.description || "",
            })
        } else {
            setEditingType(null)
            setFormData({ name: "", description: "" })
        }
        setOpenDialog(true)
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
        setEditingType(null)
        setFormError("")
    }

    const handleSave = async () => {
        if (!companyId) return
        if (!formData.name.trim()) {
            setFormError(String(t("customerTypesAdmin.errors.nameRequired")))
            return
        }
        setFormError("")
        try {
            if (editingType) {
                await customerTypesService.updateCustomerType(
                    companyId,
                    editingType.id,
                    formData
                )
            } else {
                await customerTypesService.createCustomerType(
                    companyId,
                    formData
                )
            }
            handleCloseDialog()
            loadCustomerTypes()
        } catch (error: any) {
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                String(t("customerTypesAdmin.errors.generic"))
            setFormError(msg)
        }
    }

    const handleOpenDeleteDialog = (ct: any) => {
        setDeleteError("")
        setDeletingType(ct)
        setDeleteDialogOpen(true)
    }

    const handleDelete = async () => {
        if (!companyId || !deletingType) return
        setDeleteError("")
        try {
            await customerTypesService.deleteCustomerType(
                companyId,
                deletingType.id
            )
            setDeleteDialogOpen(false)
            setDeletingType(null)
            loadCustomerTypes()
        } catch (error: any) {
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                String(t("customerTypesAdmin.errors.generic"))
            setDeleteError(msg)
        }
    }

    return (
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Container maxWidth="md">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Typography variant="h4">
                        {t("customerTypesAdmin.title")}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        {t("customerTypesAdmin.addType")}
                    </Button>
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={thStyle}>
                                    {t("customerTypesAdmin.columns.name")}
                                </TableCell>
                                <TableCell sx={thStyle}>
                                    {t(
                                        "customerTypesAdmin.columns.description"
                                    )}
                                </TableCell>
                                <TableCell sx={thStyle} align="right">
                                    {t("customerTypesAdmin.columns.actions")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {customerTypes.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={3}
                                        align="center"
                                        sx={{ py: 4, color: "#9ca3af" }}
                                    >
                                        {t("customerTypesAdmin.empty")}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                customerTypes.map((ct) => (
                                    <TableRow key={ct.id} hover>
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            {ct.name}
                                        </TableCell>
                                        <TableCell>
                                            {ct.description || t("common.dash")}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleOpenDialog(ct)
                                                }
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                    handleOpenDeleteDialog(ct)
                                                }
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Add/Edit Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {editingType
                            ? t("customerTypesAdmin.dialog.editTitle")
                            : t("customerTypesAdmin.dialog.addTitle")}
                    </DialogTitle>
                    <DialogContent>
                        {formError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {formError}
                            </Alert>
                        )}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                mt: 1,
                            }}
                        >
                            <TextField
                                label={String(
                                    t("customerTypesAdmin.fields.name")
                                )}
                                required
                                fullWidth
                                value={formData.name}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                                autoFocus
                            />
                            <TextField
                                label={String(
                                    t("customerTypesAdmin.fields.description")
                                )}
                                fullWidth
                                multiline
                                rows={2}
                                value={formData.description}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        description: e.target.value,
                                    })
                                }
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>
                            {t("common.cancel")}
                        </Button>
                        <Button onClick={handleSave} variant="contained">
                            {editingType
                                ? t("common.save")
                                : t("customerTypesAdmin.actions.create")}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                >
                    <DialogTitle>
                        {t("customerTypesAdmin.deleteDialog.title")}
                    </DialogTitle>
                    <DialogContent>
                        {deleteError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {deleteError}
                            </Alert>
                        )}
                        <DialogContentText>
                            {t("customerTypesAdmin.deleteDialog.message", {
                                name: deletingType?.name || "",
                            })}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>
                            {t("common.cancel")}
                        </Button>
                        <Button
                            onClick={handleDelete}
                            color="error"
                            variant="contained"
                        >
                            {t("customerTypesAdmin.actions.delete")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    )
}

export default CustomerTypesAdminPage
